import React, { useState } from 'react';
import StudentCheckbox from '../components/BarcodeExporter/StudentCheckbox';
import JsBarcode from 'jsbarcode';
import pdfmake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { useStudentsState } from '../contexts/StudentsContext';

const BarcodeExporter = () => {
  const [selectedStudents, setSelectedStudents] = useState<{ [barcode: string]: string }>({});

  const { students } = useStudentsState();
  const STUDENT_PAGE_LIMIT = 18;
  const generatePDF = () => {
    type Cell =
      | {
          text: string;
          alignment?: string;
          border?: boolean[];
          fontSize?: number;
          lineHeight?: number;
        }
      | { image: string; alignment?: string; width?: number; border?: boolean[]; height?: number }
      | string;
    let content: {
      table: {
        body: Cell[][];
      };
    }[] = [{ table: { body: [] } }];
    let columns: Cell[] = [];
    let nameColumns: Cell[] = [];
    const emptyCell = { text: '', border: [false, false, false, false], width: 10};
    const contents = [];
    const sortedStudents = Object.entries(selectedStudents).sort((student1, student2) => {
      return student1[1].localeCompare(student2[1]);
    });
    sortedStudents.forEach(([barcode, name], i) => {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, barcode, {
        format: 'code128',
        height: 35,
        displayValue: false,
      });
      const dataURL = canvas.toDataURL('image/png');
      nameColumns.push({
        text: name,
        alignment: 'center',
        border: [false, false, false, false],
        fontSize: 15,
        height: 100,
        margin: [0, 7, 0, 0],
      });
      nameColumns.push({ ...emptyCell });
      columns.push({
        image: dataURL,
        alignment: 'center',
        width: 206,
        border: [false, false, false, false],
        height: 37,
      });
      columns.push({ ...emptyCell, lineHeight: 3.0 });
      if (i % 2 === 1) {
        nameColumns.pop();
        columns.pop();
        content[0].table.body.push(nameColumns);
        content[0].table.body.push(columns);
        content[0].table.body.push([
          { text: '', colSpan: 3, border: [false, false, false, false], margin: [0, 4.5, 0, 0] },
          {},
          {},
        ]);
        columns = [];
        nameColumns = [];
      }

      if ((i + 1) % STUDENT_PAGE_LIMIT === 0) {
        contents.push(content);

        content = [{ table: { body: [] } }];
        columns = [];
        nameColumns = [];
      }
    });
    if (columns.length > 0) {
      content[0].table.body.push([...nameColumns, { ...emptyCell }]);
      content[0].table.body.push([...columns, { ...emptyCell, lineHeight: 3.0 }]);
    }

    if (content[0].table.body.length > 0) {
      contents.push(content);
    }

    const mergedContent = contents.flatMap((pageContent, index) => {
      const isLast = index === contents.length - 1;

      return isLast
        ? [...pageContent]
        : [
          ...pageContent,
          { text: "", pageBreak: "after" as const }, // forces next page
        ];
    });

    const docDefinition = { content: mergedContent, pageMargins: [86, 35.5, 86, 0], pageSize: 'LETTER' };

    pdfmake.vfs = pdfFonts.vfs;

    // @ts-ignore
    const pdf = pdfmake.createPdf(docDefinition);
    pdf.open();
  };

  const toggleCheckbox = (barcode: string, name: string) => {
    const newState = { ...selectedStudents };
    if (barcode in selectedStudents) {
      delete newState[barcode];
    } else {
      newState[barcode] = name;
    }
    setSelectedStudents(newState);
  };

  return (
    <div className="flex justify-between">
      <StudentCheckbox
        onCheckboxClicked={() => {
          if (Object.entries(selectedStudents).length === Object.entries(students).length) {
            setSelectedStudents({});
          } else {
            const allSelectedStudents = students.reduce(
              (acc: { [barcode: string]: string }, curr) => {
                acc[curr.barcode] = `${curr.firstName} ${curr.lastName}`;
                return acc;
              },
              {},
            );
            setSelectedStudents(allSelectedStudents);
          }
        }}
        checkboxValue={Object.entries(selectedStudents).length === Object.entries(students).length}
        firstName="Select All"
        lastName=""
        parentPhone=""
      />
      <div className="columns-2 w-3/4">
        {students
          .sort((student1, student2) => {
            return student1.firstName.localeCompare(student2.firstName);
          })
          .map(({ firstName, lastName, primaryParentPhone, barcode }) => {
            return (
              <StudentCheckbox
                key={`studentcheckbox-${firstName}-${lastName}`}
                onCheckboxClicked={() => toggleCheckbox(barcode, `${firstName} ${lastName}`)}
                checkboxValue={barcode in selectedStudents}
                firstName={firstName}
                lastName={lastName}
                parentPhone={primaryParentPhone}
              />
            );
          })}
      </div>
      <button
        onClick={generatePDF}
        className="flex justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 mt-2 h-12"
      >
        Generate PDF
      </button>
    </div>
  );
};

export default BarcodeExporter;
